import { ResponsivePie } from "@nivo/pie";
import api from "@/components/customAxios/Axios";
import { useToast } from "@/components/toast";
import { useLoader } from "@/context/loaderContext";
import { useEffect, useState } from "react";

type dataType = {
	assessment: {
		max: number;
		used: number;
	};
	cv: {
		max: number;
		used: number;
	};
	users: {
		max: number;
		active: number;
	};
};
const CompanyInsights = () => {
	const [data, setData] = useState<dataType>();
	const [pieChartData, setPieChartData] = useState(() => {
		return [
			{
				id: "Resume Used",
				label: "Resume Used",
				value: 0,
				color: "",
			},
			{
				id: "Resume Left",
				label: "Resume Left",
				value: 0,
				color: "",
			},
			{
				id: "Assessments Used",
				label: "Assessments Used",
				value: 0,
				color: "",
			},
			{
				id: "Assessments Left",
				label: "Assessments Left",
				value: 0,
				color: "",
			},
			{
				id: "User Used",
				label: "User Used",
				value: 0,
				color: "#C9AEED",
			},
			{
				id: "User Left",
				label: "User Left",
				value: 0,
				color: "#E5E7EB",
			},
		];
	});

	const { setLoading } = useLoader();
	const toast = useToast();

	const getQuickData = async () => {
		try {
			setLoading(true);
			const response = await api.get("/dashboard/subscription/insights");

			setData(response.data.usage);
		} catch (err: any) {
			setLoading(false);
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Error Fetching Insights",
				description: err.response?.data?.error || err.response?.data?.message || "Failed to fetch company insights. Please try again.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getQuickData();
	}, []);

	useEffect(() => {
		if (!data) return;

		const assessmentUsed = data.assessment.used;
		const assessmentTotal = data.assessment.max;
		const cvUsed = data.cv.used;
		const cvTotal = data.cv.max;
		const userUsed = data.users.active;
		const userTotal = data.users.max;
		setPieChartData([
			{
				id: "Resume Used",
				label: "Resume Used",
				value: cvUsed,
				color: "#6EE7B7",
			},
			{
				id: "Resume Left",
				label: "Resume Left",
				value: cvTotal - cvUsed,
				color: "#E5E7EB",
			},
			{
				id: "Assessments Used",
				label: "Assessments Used",
				value: assessmentUsed,
				color: "#93C5FD",
			},
			{
				id: "Assessments Left",
				label: "Assessments Left",
				value: assessmentTotal - assessmentUsed,
				color: "#E5E7EB",
			},
			{
				id: "User Used",
				label: "User Used",
				value: userUsed,
				color: "#C9AEED",
			},
			{
				id: "User Left",
				label: "User Left",
				value: userTotal - userUsed,
				color: "#E5E7EB",
			},
		]);
	}, [data]);

	return (
		<>
			<div className=" bg-white rounded-xl p-6  ">
				<h2 className="text-lg font-semibold mb-6">Subscription Insights</h2>
				{/* Chart container */}
				<div className="flex flex-col  items-center justify-center gap-10">
					<div className="relative size-64">
						{/* Resume Doughnut */}
						<div className="absolute inset-0">
							<ResponsivePie
								data={pieChartData.slice(0, 2)}
								innerRadius={0.8}
								padAngle={0}
								cornerRadius={3}
								enableArcLabels={false}
								enableArcLinkLabels={false}
								colors={(d) => d.data.color}
								startAngle={-135}
								endAngle={225}
								sortByValue={true}
								margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
								animate={false}
							/>
						</div>

						{/* Assessment Doughnut */}
						<div className="absolute inset-10">
							{" "}
							{/* Changed from inset-6 to inset-8 */}
							<ResponsivePie
								data={pieChartData.slice(2, 4)}
								innerRadius={0.7}
								padAngle={0}
								cornerRadius={3}
								enableArcLabels={false}
								enableArcLinkLabels={false}
								colors={(d) => d.data.color}
								startAngle={-135}
								endAngle={225}
								sortByValue={true}
								margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
								animate={false}
							/>
						</div>

						{/* User Doughnut */}
						<div className="absolute inset-20">
							{" "}
							{/* Changed from inset-12 to inset-16 */}
							<ResponsivePie
								data={pieChartData.slice(4, 6)}
								innerRadius={0.6}
								padAngle={0}
								cornerRadius={3}
								enableArcLabels={false}
								enableArcLinkLabels={false}
								colors={(d) => d.data.color}
								startAngle={-135}
								endAngle={225}
								sortByValue={true}
								margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
								animate={true}
							/>
						</div>
					</div>

					{/* Legend */}
					<div className="   text-sm flex  justify-around items-center w-full flex-wrap gap-y-3">
						<div className="flex items-center gap-2">
							<span className="w-3 h-3 bg-green-300 rounded-full"></span> Resume
							Screeing {data?.cv.used} / {data?.cv.max}
						</div>
						<div className="flex items-center gap-2">
							<span className="w-3 h-3 bg-blue-300 rounded-full"></span>{" "}
							Assessments {data?.assessment.used} / {data?.assessment.max}
						</div>
						<div className="flex items-center gap-2">
							<span className="w-3 h-3 bg-[#C9AEED] rounded-full"></span> Users{" "}
							{data?.users.active} / {data?.users.max}
						</div>
					</div>
				</div>
			</div>
		</>
	);

	// return (
	// 	<div className="flex flex-col  justify-between gap-3 ">
	// 		{/* Pie Chart Section */}
	// 		<div
	// 			className="bg-white  rounded-lg p-6 w-full space-y-6 h-[400px]"
	// 			// data-aos="flip-right"
	// 		>
	// 			{/* Header Section */}
	// 			<div className="flex items-center justify-between ">
	// 				<h3 className=" font-semibold text-gray-700">Company Insights</h3>
	// 				{/* <span className="  text-gray-500">Overall Satisfaction</span> */}
	// 			</div>
	// 			<div className="flex items-center justify-between">
	// 				{/* Chart Section */}{" "}
	// 				<div className="relative h-[300px] w-[300px]">
	// 					<ResponsivePie
	// 						data={pieChartData}
	// 						margin={{ top: 40, right: 10, bottom: 80, left: 90 }}
	// 						innerRadius={0.38}
	// 						padAngle={0}
	// 						cornerRadius={5}
	// 						activeOuterRadiusOffset={8}
	// 						colors={{ scheme: "nivo" }}
	// 						borderWidth={1}
	// 						borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
	// 						arcLinkLabelsSkipAngle={10}
	// 						arcLinkLabelsTextColor="#333333"
	// 						arcLinkLabelsThickness={2}
	// 						arcLinkLabelsColor={{ from: "color" }}
	// 						arcLabelsSkipAngle={10}
	// 						arcLabelsTextColor={{
	// 							from: "color",
	// 							modifiers: [["darker", 2]],
	// 						}}
	// 					/>

	// 					{/* Centered Label */}
	// 				</div>
	// 				{/* Legend Section */}
	// 				<div className="flex-col space-y-3 mr-3 ">
	// 					<div className="flex items-center space-x-2">
	// 						<span className=" text-gray-700">Cvs :-</span>
	// 						<span className=" text-gray-700">
	// 							{data?.cv.used}/ {data?.cv.max}
	// 						</span>
	// 					</div>
	// 					<div className="flex items-center space-x-2">
	// 						<span className=" text-gray-700">Assessment</span>
	// 						<span className=" text-gray-700">
	// 							{data?.assessment.used}/ {data?.assessment.max}
	// 						</span>
	// 					</div>
	// 					<div className="flex items-center space-x-2">
	// 						<span className=" text-gray-700">Users</span>
	// 						<span className=" text-gray-700">
	// 							{data?.users.active}/ {data?.users.max}
	// 						</span>
	// 					</div>
	// 				</div>
	// 			</div>
	// 		</div>

	// 		{/* Bar Chart Section */}
	// 		<div
	// 			className=" bg-white  rounded-lg p-4 w-full h-[350px]"
	// 			// data-aos="flip-left"
	// 		>
	// 			<div className="flex justify-between items-center mb-4">
	// 				<h3 className="text-xl font-semibold text-gray-700">
	// 					Hiring Insights
	// 				</h3>
	// 				<h6 className="text-lg  text-gray-500">Coming Soon</h6>
	// 			</div>
	// 			{/* {graphData ? (
	//                 <div className="h-[200px] w-full ">
	//                     <ResponsiveBar
	//                         data={starData}
	//                         keys={["star"]}
	//                         indexBy="starNumber"
	//                         layout="vertical"
	//                         margin={{ top: 5, right: 10, bottom: 20, left: 50 }}
	//                         padding={0.2}
	//                         colors={({ index }) => colors[index]} // Assign different colors for each bar
	//                         borderRadius={10} // Rounded corners for the bars
	//                         borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
	//                         axisTop={null}
	//                         axisRight={null}
	//                         axisBottom={{
	//                             tickSize: 0,
	//                             tickPadding: 5,
	//                             tickRotation: 0,
	//                             legend: "",
	//                             legendPosition: "middle",
	//                             legendOffset: 32,
	//                         }}
	//                         axisLeft={{
	//                             tickSize: 0,
	//                             tickPadding: 10,
	//                             tickRotation: 0,
	//                             legend: "",
	//                             legendPosition: "middle",
	//                             legendOffset: -40,
	//                         }}
	//                         enableGridX={false} // Enable the grid for x-axis
	//                         enableGridY={false} // Disable the grid for y-axis
	//                         // label={(d) => d.value} // Display the value inside the bars
	//                         labelTextColor="#FFFFFF" // Label color
	//                         labelSkipWidth={0}
	//                         labelSkipHeight={0}
	//                         theme={{
	//                             axis: {
	//                                 ticks: {
	//                                     text: {
	//                                         fontSize: 12,
	//                                         fill: "#6B7280", // Gray text for ticks
	//                                     },
	//                                 },
	//                             },
	//                             grid: {
	//                                 line: {
	//                                     stroke: "#E5E7EB", // Light gray for grid lines
	//                                     strokeWidth: 1,
	//                                 },
	//                             },
	//                         }}
	//                     />
	//                 </div>
	//             ) : (
	//                 <div className="p-3 text-center font-semibold text-2xl text-gray-500">
	//                     No Data Found
	//                 </div>
	//             )} */}
	// 		</div>
	// 	</div>
	// );
};

export default CompanyInsights;
